import React, { useMemo, useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useMsal } from "@azure/msal-react";

import IconHome from "../../../../styles/icons/IconHome";
import { selectedLead } from "../../../../slices/leads/leadManagementSlice";
import ActionModal from "../../../molecules/modal/ActionModal";
import useUserRole from "../../../../hooks/useUserRole";
import { UserRole } from "../../../../utils";
import { SpecificLead } from "../../../../slices/leads/types";

export type CardFieldKey =
  | keyof SpecificLead
  | "createdDate"
  | "startDate"
  | "endDate"
  | "leadDate"
  | "customer"
  | "Action";

export interface CardContentItem {
  name: string;
  dbName: CardFieldKey;
  fullWidth?: boolean;
}

interface Props {
  cardContent: CardContentItem[];
  data: SpecificLead;
  deleteCard: (data: SpecificLead) => void;
  isSelected?: boolean;
  onSelect?: () => void;
  isLeads?: boolean;
  retryLead?: (data: SpecificLead) => void;
  isShowAction?: boolean;
  canEdit?: boolean;
  pathUrl?: string;
  renderCustomField?: (key: string, data: any) => React.ReactNode;
  title?: string;
  avatarText?: string;
  emailLabel?: string;
}

const FullWidthCard: React.FC<Props> = ({
  cardContent,
  data,
  deleteCard,
  isSelected = false,
  onSelect,
  isLeads = false,
  retryLead,
  isShowAction = true,
  canEdit = true,
  pathUrl = "",
  title,
  avatarText,
  emailLabel = "Email",
}) => {
  const [isConfirmationModalShow, setIsConfirmationModalShow] = useState(false);

  const location = useLocation();
  const dispatch = useDispatch();
  const { instance } = useMsal();
  const currentUserRole = useUserRole();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const activeAccount = instance?.getActiveAccount();
  const msalUserRole =
    (activeAccount?.idTokenClaims as { extension_ilpr?: string } | undefined)
      ?.extension_ilpr || "";

  const pathname = location.pathname;
  const id = data.id;

  const isJobsPage = pathname === "/jobs";
  const isCustomersPage = pathname === "/customers";
  const isJobsOrClientsPage = isJobsPage || isCustomersPage;

  const filteredCardContent = useMemo(() => {
    let updatedCardContent = cardContent.filter(
      (item) =>
        item.dbName !== "rating" &&
        item.dbName !== "id" &&
        item.dbName !== "email"
    );

    if (msalUserRole === UserRole.Operator) {
      updatedCardContent = updatedCardContent.filter(
        (item) => item.dbName !== "rating"
      );
    }

    return updatedCardContent;
  }, [cardContent, msalUserRole]);

  const detailPath = useMemo(() => {
    if (
      pathname === "/leads/verified-leads" ||
      pathname === "/leads/rejected-leads" ||
      pathname === "/leads/error-leads"
    ) {
      return `/leads/lead-details/${id}`;
    }
    if (pathUrl) {
      return `/${pathUrl.replace(/^\/+/, "")}/${id}`;
    }

    return `${pathname}/${id}`;
  }, [pathname, id, pathUrl]);

  const editPath = useMemo(() => `${pathname}/edit/${id}`, [pathname, id]);

  const headerText = useMemo(() => {
    if (title !== undefined) return title;
    if (isCustomersPage) return data.companyName || "";
    if (isJobsPage) return data.name || "";

    const firstAssignedUser = data.assignedUsers?.[0];
    if (!firstAssignedUser) return "";

    return `${firstAssignedUser.companyName || ""} | ${firstAssignedUser.firstName || ""
      } ${firstAssignedUser.lastName || ""}`.trim();
  }, [data, isCustomersPage, isJobsPage, title]);

  const handleOpenModal = useCallback(() => {
    setIsConfirmationModalShow(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsConfirmationModalShow(false);
  }, []);

  const handleSetItem = useCallback(() => {
    dispatch(selectedLead(data));
  }, [dispatch, data]);

  const handleConfirm = useCallback(() => {
    handleCloseModal();
    deleteCard(data);
  }, [handleCloseModal, deleteCard, data]);

  const getListItemHeading = (name: string) => {
    return name === "Action" ? "" : name;
  };

  const renderRatingBadge = () => {
    if (!data.rating) return null;

    return (
      <span
        className={`chip chip--soft chip--status-${data.rating
          .toLowerCase()
          .replace(/\s+/g, "")}`}
      >
        {data.rating}
      </span>
    );
  };

  const renderFieldValue = (name: string, dbName: CardFieldKey) => {
    if (
      name === "Action" ||
      dbName === "Action" ||
      dbName === "rating" ||
      dbName === "id" ||
      dbName === "email"
    ) {
      return null;
    }

    switch (dbName) {
      case "createdDate":
        return data.createdAt || null;

      case "firstName":
        return `${data.firstName || ""} ${data.lastName || ""}`.trim() || null;

      case "startDate":
        return data.startDate || null;

      case "endDate":
        return data.endDate || null;

      case "leadDate":
        return data.createdAt || null;

      case "customer":
        return data.customer?.companyName || null;

      default: {
        const value = data[dbName as keyof SpecificLead];

        if (value === null || value === undefined || value === "") {
          return null;
        }

        if (typeof value === "object") {
          return null;
        }

        return String(value);
      }
    }
  };

  const isEditAllowedByRole =
    !isJobsOrClientsPage || currentUserRole !== UserRole.Customer;
  const showEditIcon = canEdit && isEditAllowedByRole;

  const canDelete =
    currentUserRole !== UserRole.Operator &&
    currentUserRole !== UserRole.Customer;

  const ONE_HOUR_IN_MS = 60 * 60 * 1000;

  const getLeadCreatedDateTime = (data: any): Date | null => {
    if (!data.createdAt || !data.createdTime) {
      return null;
    }

    const [day, month, year] = String(data.createdAt)
      .split("/")
      .map(Number);

    const [hours, minutes, seconds = 0] = String(data.createdTime)
      .split(":")
      .map(Number);

    if (!day || !month || !year) {
      return null;
    }

    // IMPORTANT:
    // Backend time is UTC
    return new Date(
      Date.UTC(year, month - 1, day, hours, minutes, seconds)
    );
  };

  const normalizeRating = String(data.rating || "")
    .toLowerCase()
    .replace(/\s+/g, "");

  const fullCreatedDateTime = getLeadCreatedDateTime(data);

  const isCreatedMoreThanOneHourAgo =
    fullCreatedDateTime !== null &&
    new Date().getTime() - fullCreatedDateTime.getTime() >=
    ONE_HOUR_IN_MS;

  // Logic:
  // SystemError => always show retry
  // InProgress => show retry only after 1 hour
  const shouldShowRetryButton =
    normalizeRating === "systemerror" ||
    (normalizeRating === "inprogress" &&
      isCreatedMoreThanOneHourAgo);

  const renderIcons = () => (
    <div className="bottom-section__icons">

      {isLeads && retryLead && shouldShowRetryButton && (
        <button
          title="Retry Lead"
          aria-label="Retry lead"
          type="button"
          className="retry-lead-btn"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            retryLead(data);
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 11A8.1 8.1 0 0 0 4.5 9M4 5v4h4" />
            <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4" />
          </svg>
        </button>
      )}

      <Link to={detailPath} title="View Details" onClick={handleSetItem}>
        <IconHome breadcrumb="view" activeColor={undefined} />
      </Link>
      

      {showEditIcon && (
        <Link to={editPath} title="Edit Item" onClick={handleSetItem}>
          <IconHome breadcrumb="edit" activeColor={undefined} />
        </Link>
      )}

      {canDelete && (
        <div
          onClick={handleOpenModal}
          title="Delete Item"
          style={{ cursor: "pointer" }}
        >
          <IconHome breadcrumb="delete" activeColor={undefined} />
        </div>
      )}
    </div>
  );

  return (
    <div className="full-width-card">
      <div className="content-section">
        <div className="top-section">
          <div className="top-section__right">
            <div
              className={`top-section__id${avatarText ? " top-section__id--avatar" : ""}`}
            >
              {avatarText ?? data.id}
            </div>
            <div className="top-section__company">{headerText}</div>
          </div>

          {isLeads && (
            <div className="top-section__checkbox">
              <div className="bottom-section__left">{renderRatingBadge()}</div>
              <input
                type="checkbox"
                checked={isSelected}
                onChange={onSelect}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
        </div>

        <div className="middle-section mt-4">
          <div className="middle-section__list">
            {filteredCardContent.map((item, index) => {
              const content = renderFieldValue(item.name, item.dbName);

              if (!content) return null;

              return (
                <div
                  className={`list-item${item.fullWidth ? " list-item--full" : ""}`}
                  key={`${String(item.dbName)}-${index}`}
                  onClick={handleSetItem}
                >
                  <div className="list-item-heading">
                    {getListItemHeading(item.name)}
                  </div>
                  <div className="list-item-content">{content}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bottom-section">
          <div className="bottom-section__email" title={data.email}>
            {data.email ? `${emailLabel}: ${data.email}` : ""}
          </div>
          {isShowAction && renderIcons()}
        </div>
      </div>

      <ActionModal
        title="Delete Item"
        show={isConfirmationModalShow}
        onClose={handleCloseModal}
        onConfirm={handleConfirm}
      >
        <p className="modal-content">
          Are you sure you want to delete this item?
        </p>
      </ActionModal>
    </div>
  );
};

export default FullWidthCard;
